// src/hooks/useRowSelection.js
import { useCallback, useMemo, useState } from "react";

const defaultGetId = (x) => x?.id;

export default function useRowSelection(data = [], getId = defaultGetId) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const keyOf = useCallback(
    (val) => {
      // Accept either an id or an item
      if (typeof val === "object" && val !== null) return String(getId(val));
      return String(val);
    },
    [getId],
  );

  const pageIds = useMemo(() => data.map((row) => keyOf(row)), [data, keyOf]);

  const selectedCount = selectedIds.size;

  const allSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const someSelected =
    pageIds.some((id) => selectedIds.has(id)) && !allSelected;

  const isSelected = useCallback(
    (id) => selectedIds.has(keyOf(id)),
    [selectedIds, keyOf],
  );

  const toggleOne = useCallback(
    (id) => {
      const key = keyOf(id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
      });
    },
    [keyOf],
  );

  const toggleMany = useCallback(
    (ids = []) => {
      const keys = ids.map((id) => keyOf(id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        const allAlready = keys.every((k) => next.has(k));
        keys.forEach((k) => (allAlready ? next.delete(k) : next.add(k)));
        return next;
      });
    },
    [keyOf],
  );

  const togglePage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allAlready = pageIds.every((k) => next.has(k));
      pageIds.forEach((k) => (allAlready ? next.delete(k) : next.add(k)));
      return next;
    });
  }, [pageIds]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const clear = clearSelection; // ✅ alias so components can do clear: clearSelection

  return {
    selectedIds,
    setSelectedIds,
    selectedCount,
    isSelected,
    toggleOne,
    toggleMany,
    togglePage,
    clearSelection,
    clear,
    allSelected,
    someSelected,
  };
}
